import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Accessibility = () => (
  <div className="min-h-screen">
    <Header />
    <main id="main-content" className="pt-24 pb-16 container mx-auto px-4 max-w-4xl">
      <h1 className="text-4xl font-bold mb-8">Accessibility Statement</h1>
      <div className="prose prose-lg text-muted-foreground space-y-6">
        <p><strong>Last updated:</strong> March 2026</p>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mt-8">Our Commitment</h2>
          <p>
            Coblynau IT Solutions is committed to ensuring that our website is accessible to
            everyone, including people with disabilities. We aim to meet the Web Content
            Accessibility Guidelines (WCAG) 2.2 at Level AA as a minimum standard.
          </p>
          <p>
            We recognise our obligations under the{" "}
            <a
              href="https://www.legislation.gov.uk/ukpga/2010/15/contents"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Equality Act 2010
            </a>{" "}
            to make reasonable adjustments so that disabled people are not placed at a substantial
            disadvantage when using our services. This commitment extends to our digital presence,
            and we consider web accessibility a key part of meeting those obligations.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mt-8">Technical Standards</h2>
          <p>This website aims to conform to WCAG 2.2 Level AA. This includes:</p>
          <ul className="list-disc pl-6">
            <li>Perceivable: content is presented in ways all users can perceive, including text alternatives for images and sufficient colour contrast</li>
            <li>Operable: all functionality is available from a keyboard, with visible focus indicators and no content that could cause seizures</li>
            <li>Understandable: content is readable, predictable, and helps users avoid and correct mistakes</li>
            <li>Robust: content is compatible with current and future assistive technologies</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mt-8">What We Do</h2>
          <p>We take the following steps to support accessibility on our website:</p>
          <ul className="list-disc pl-6">
            <li>Use clear, simple language throughout the site</li>
            <li>Ensure sufficient colour contrast between text and backgrounds (meeting WCAG AA 4.5:1 for text and 3:1 for non-text elements)</li>
            <li>Provide text alternatives for meaningful images</li>
            <li>Ensure the website is fully navigable using a keyboard, with visible focus indicators</li>
            <li>Provide a &quot;skip to main content&quot; link for keyboard users</li>
            <li>Use semantic HTML to support assistive technologies such as screen readers</li>
            <li>Ensure all interactive elements have a minimum target size of 24×24 pixels</li>
            <li>Design responsive layouts that work across different devices and screen sizes</li>
            <li>Respect user preferences for reduced motion</li>
            <li>Ensure focused elements are not obscured by fixed page elements</li>
            <li>Avoid reliance on external resources that may compromise performance or privacy</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mt-8">Known Limitations</h2>
          <p>
            While we strive to make every part of this website accessible, there may be areas
            that are not yet fully compliant. We are continuously working to identify and
            resolve any accessibility issues.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mt-8">Feedback</h2>
          <p>
            If you experience any difficulty accessing content on this website, or if you have
            suggestions for how we can improve accessibility, please contact us:
          </p>
          <p>
            Email:{" "}
            <a href="mailto:info@coblynausolutions.co.uk" className="text-primary hover:underline">
              info@coblynausolutions.co.uk
            </a>
          </p>
          <p>
            You can also use our{" "}
            <Link to="/contact" className="text-primary hover:underline">contact form</Link>.
          </p>
          <p>We aim to respond to accessibility feedback within 5 working days.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mt-8">Enforcement</h2>
          <p>
            The Equality Act 2010 provides legal protection against discrimination for disabled
            people. If you believe you have been discriminated against, you may have a legal right
            to take action.
          </p>
          <p>
            If you are not satisfied with our response, you can contact the{" "}
            <a
              href="https://www.equalityadvisoryservice.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Equality Advisory and Support Service (EASS)
            </a>, which provides advice and support on discrimination and human rights issues.
          </p>
        </section>
      </div>
    </main>
    <Footer />
  </div>
);

export default Accessibility;
